import React, {useCallback, useEffect, useState} from 'react';
import {
    IonButton,
    IonButtons,
    IonContent,
    IonHeader,
    IonInput,
    IonItem,
    IonLabel,
    IonList,
    IonMenuButton,
    IonPage,
    IonTitle,
    IonToolbar
} from '@ionic/react';
import createAxiosInstance from "../../services/AxiosInstance";
import EmailInput from "../../components/auth/EmailInput";
import { useToast } from '../../utils/useToasts';
import {
    disablePushNotifications,
    getPushNotificationsStatus,
    PushNotificationsStatus,
    registerPushNotifications,
} from '../../services/pushNotifications';

const MIN_NAME_LENGTH = 4;
const DUPLICATE_PROFILE_STATUS = 400;

const INITIAL_PUSH_STATUS: PushNotificationsStatus = {
    isSupported: false,
    hasFirebaseConfig: false,
    permission: 'unsupported',
    hasStoredToken: false,
};

const getPushStatusLabel = (status: PushNotificationsStatus): string => {
    if (!status.isSupported) {
        return 'Deze browser ondersteunt geen pushmeldingen voor de PWA.';
    }

    if (!status.hasFirebaseConfig) {
        return 'Firebase pushconfiguratie ontbreekt nog in de frontend-omgeving.';
    }

    if (status.permission === 'denied') {
        return 'Meldingen zijn geblokkeerd in de browserinstellingen voor dit apparaat.';
    }

    if (status.permission === 'granted' && status.hasStoredToken) {
        return 'Dit apparaat is gekoppeld voor pushmeldingen.';
    }

    if (status.permission === 'granted') {
        return 'Toestemming is gegeven, maar dit apparaat is nog niet gekoppeld.';
    }

    return 'Vraag toestemming aan en koppel dit apparaat voor pushmeldingen.';
};

const getPushErrorMessage = (error: unknown): string => {
    if (!(error instanceof Error)) {
        return 'Pushmeldingen konden niet worden bijgewerkt.';
    }

    switch (error.message) {
        case 'missing-config':
            return 'Firebase configuratie ontbreekt. Voeg eerst de REACT_APP_FIREBASE_* variabelen toe.';
        case 'permission-denied':
            return 'Meldingen zijn geweigerd in de browser. Pas dit aan in de browserinstellingen.';
        case 'permission-dismissed':
            return 'Er is nog geen toestemming gegeven voor meldingen.';
        case 'missing-service-worker':
        case 'push-production-only':
            return 'Pushmeldingen werken pas in de productie-PWA met een actieve service worker.';
        case 'unsupported':
            return 'Deze browser ondersteunt geen pushmeldingen voor de PWA.';
        default:
            return 'Pushmeldingen konden niet worden bijgewerkt.';
    }
};

const ProfilePage: React.FC = () => {
    const [id, setId] = useState<string>('');
    const [name, setName] = useState('');
    const [email, setEmail] = useState('');
    const [emailValid, setEmailValid] = useState<boolean>(true);
    const [pushStatus, setPushStatus] = useState<PushNotificationsStatus>(INITIAL_PUSH_STATUS);
    const [pushBusy, setPushBusy] = useState(false);
    const axios = createAxiosInstance();
    const showToasts = useToast();

    const refreshPushStatus = useCallback(async () => {
        const nextStatus = await getPushNotificationsStatus();
        setPushStatus(nextStatus);
    }, []);

    const fetchPersonalInformation = async () => {
        axios.get('/user/account-settings/personal-information')
            .then(res => {
                console.log(res.data)
                setName(res.data.name);
                setEmail(res.data.emailAddress);
                setId(res.data.id);
            })
    }

    const handleSave = () => {
        if (name.length === 0 || email.length === 0) {
            showToasts('Niet alle velden zijn ingevuld', 'danger');
            return;
        }
        if (name.length < MIN_NAME_LENGTH) {
            showToasts('Naam te kort', 'danger');
            return;
        }
        if (!emailValid) {
            showToasts('Vul een geldig e-mailadres in.', 'danger');
            return;
        }

        axios.post(`/user/account-settings/personal-information/${id}`, {
            name,
            emailAddress: email
        }).then(res => {
            showToasts('Profiel opgeslagen', 'success');
        }).catch(err => {
            if (err.response.status === DUPLICATE_PROFILE_STATUS) {
                showToasts('Er is een duplicaat gevonden, probeer een andere naam of email', 'danger')
                return;
            }

            showToasts('Er is iets mis gegaan', 'danger');
        })
    }

    const handleEmailChange = useCallback((newEmail: string, isValid: boolean) => {
        setEmail(newEmail);
        setEmailValid(isValid);
    }, []);

    const handleEnablePush = useCallback(async () => {
        setPushBusy(true);

        try {
            const nextStatus = await registerPushNotifications();
            setPushStatus(nextStatus);
            showToasts('Pushmeldingen zijn ingeschakeld op dit apparaat.', 'success');
        } catch (error) {
            showToasts(getPushErrorMessage(error), 'danger');
            await refreshPushStatus();
        } finally {
            setPushBusy(false);
        }
    }, [refreshPushStatus, showToasts]);

    const handleDisablePush = useCallback(async () => {
        setPushBusy(true);

        try {
            const nextStatus = await disablePushNotifications();
            setPushStatus(nextStatus);
            showToasts('Pushmeldingen zijn uitgeschakeld op dit apparaat.', 'success');
        } catch (error) {
            showToasts(getPushErrorMessage(error), 'danger');
            await refreshPushStatus();
        } finally {
            setPushBusy(false);
        }
    }, [refreshPushStatus, showToasts]);

    useEffect(() => {
        fetchPersonalInformation();
        refreshPushStatus().catch((error) => {
            console.warn('[Push] Status ophalen mislukt.', error);
        });
    }, [refreshPushStatus]);

    return (
        <IonPage>
            <IonHeader>
                <IonToolbar>
                    <IonButtons slot="start">
                        <IonMenuButton/>
                    </IonButtons>
                    <IonTitle>Profiel Instellingen</IonTitle>
                </IonToolbar>
            </IonHeader>
            <IonContent className="ion-padding">
                <IonList>
                    <IonItem>
                        <IonLabel position="floating">Naam</IonLabel>
                        <IonInput value={name} onIonInput={e => setName(e.detail.value!)}/>
                    </IonItem>
                    <EmailInput onEmailChange={handleEmailChange} initialEmail={email}/>
                    <IonItem>
                        <IonLabel>
                            <h2>Pushmeldingen</h2>
                            <p>{getPushStatusLabel(pushStatus)}</p>
                        </IonLabel>
                    </IonItem>
                </IonList>
                <IonButton expand="block" onClick={handleSave}>Wijzigingen opslaan</IonButton>
                <IonButton
                    expand="block"
                    onClick={handleEnablePush}
                    disabled={pushBusy || !pushStatus.isSupported || !pushStatus.hasFirebaseConfig || pushStatus.permission === 'denied'}
                >
                    {pushStatus.hasStoredToken ? 'Pushmeldingen opnieuw koppelen' : 'Pushmeldingen inschakelen'}
                </IonButton>
                <IonButton
                    expand="block"
                    fill="outline"
                    color="medium"
                    onClick={handleDisablePush}
                    disabled={pushBusy || !pushStatus.hasStoredToken}
                >
                    Pushmeldingen uitschakelen
                </IonButton>
            </IonContent>
        </IonPage>
    );
};

export default ProfilePage;
